<script setup lang="ts">
import type { CurrentPage } from '~/types/mixpanel';

import { useEvents } from '@/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cn } from '~/utils';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';

import { CardImageOverlay } from '~/components/CardImageOverlay';
import {
	Carousel,
	CarouselContent,
	CarouselGenericAction,
	CarouselItem,
	CarouselItemSelector
} from '~/components/ui/carousel';

interface RegionsSectionProps {
	currentPage?: CurrentPage;
}

const props = withDefaults(defineProps<RegionsSectionProps>(), {
	currentPage: 'HomePage'
});

interface RegionCardInfo {
	name: string;
	subtitle: string;
	imageUrl: string;
	macroregion: string;
	path: string;
}

interface RawRegionCardLinkInfo extends Omit<RegionCardInfo, 'path'> {
	state: string;
}

function getFormattedRegionCardInfo(
	region: RawRegionCardLinkInfo
): RegionCardInfo {
	const path = PropertyFiltersPresenter.getPathFromFilters(
		PropertyFiltersPresenter.createLocationFilters({
			macroregions: [region.macroregion],
			states: [region.state]
		})
	);

	return {
		name: region.name,
		subtitle: region.subtitle,
		imageUrl: region.imageUrl,
		macroregion: region.macroregion,
		path
	};
}

const rawRegions: RawRegionCardLinkInfo[] = [
	{
		name: 'São Paulo',
		subtitle: 'Viver no coração da cidade que nunca para.',
		imageUrl: '/images/regions-section/sao-paulo.webp',
		macroregion: 'São Paulo',
		state: 'SP'
	},
	{
		name: 'Curitiba',
		subtitle: 'Imóveis em meio ao charme e à qualidade de vida curitibana.',
		imageUrl: '/images/regions-section/curitiba.webp',
		macroregion: 'Curitiba',
		state: 'PR'
	},
	{
		name: 'Interior SP',
		subtitle: 'Condomínios que unem natureza, espaço e tranquilidade.',
		imageUrl: '/images/regions-section/interior-sp.webp',
		macroregion: 'Interior',
		state: 'SP'
	},
	{
		name: 'Alphaville SP',
		subtitle: 'Um endereço que combina privacidade e conforto.',
		imageUrl: '/images/regions-section/alphaville-sp.webp',
		macroregion: 'Alphaville',
		state: 'SP'
	},
	{
		name: 'Litoral Norte SP',
		subtitle: 'Casas em meio a natureza preservada e o mar azul.',
		imageUrl: '/images/regions-section/litoral-norte-sp.webp',
		macroregion: 'Litoral Norte',
		state: 'SP'
	},
	{
		name: 'Litoral Sul SP',
		subtitle: 'Endereços à beira-mar com toda estrutura.',
		imageUrl: '/images/regions-section/litoral-sul-sp.webp',
		macroregion: 'Litoral Sul',
		state: 'SP'
	}
];

const regions: RegionCardInfo[] = rawRegions.map(region =>
	getFormattedRegionCardInfo(region)
);

const carouselContainer = ref<InstanceType<typeof Carousel> | null>(null);
const carouselItemSelectorContainer = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef: carouselContainer,
	carouselItemSelectorContainerRef: carouselItemSelectorContainer,
	cyclic: true
});

const { trackRegionCardClicked } = useEvents();

function handleRegionCardClick(region: RegionCardInfo): void {
	trackRegionCardClicked({
		[MixpanelProps.CURRENT_PAGE]: props.currentPage,
		[MixpanelProps.SECTION_NAME]: 'regions',
		[MixpanelProps.DESTINATION_URL]: region.path,
		[MixpanelProps.REGION_NAME]: region.macroregion
	});
}

function handlePreviousSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value - 1);
}

function handleNextSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value + 1);
}

const SECTION_PADDING_X_CLASSES = 'px-5 md:px-8 lg:px-12 2xl:px-0';
</script>

<template>
	<section>
		<div
			class="mx-auto flex w-full flex-col items-start gap-6 bg-primary py-11 text-white md:gap-9 md:py-11 lg:py-15 xl:py-[5.38rem] 2xl:max-w-[1440px]"
		>
			<header>
				<h2
					class="font-matter-sq text-[1.75rem] font-normal leading-[150%] tracking-[-0.035rem] lg:text-4xl lg:tracking-[-0.045rem]"
					:class="SECTION_PADDING_X_CLASSES"
				>
					Nossas regiões
				</h2>
			</header>

			<Carousel
				ref="carouselContainer"
				class="flex w-full flex-col gap-5 md:gap-9 lg:px-12 2xl:px-0"
				:plugins="[WheelGesturesPlugin() as any]"
				:opts="{
					align: 'center',
					slidesToScroll: 1,
					breakpoints: {
						'(min-width: 1024px)': { align: 'start' },
						'(min-width: 1536px)': { align: 'start' }
					}
				}"
			>
				<CarouselContent
					class="flex gap-5"
					:class="cn(SECTION_PADDING_X_CLASSES, 'lg:px-0')"
				>
					<CarouselItem
						v-for="region in regions"
						:key="region.name"
						class="h-80 basis-[94%] overflow-hidden md:w-85.5 md:basis-[48.8%] lg:basis-[calc(33.33333%-1rem)] xl:h-100 2xl:basis-[24%]"
					>
						<CardImageOverlay
							:image="{
								src: region.imageUrl,
								alt: `Região de ${region.name}`
							}"
							:content="{
								title: region.name,
								subtitle: region.subtitle
							}"
							:to="region.path"
							@click="handleRegionCardClick(region)"
						/>
					</CarouselItem>
				</CarouselContent>

				<div
					class="flex w-full items-center gap-5"
					:class="cn(SECTION_PADDING_X_CLASSES, 'lg:px-0')"
				>
					<div
						ref="carouselItemSelectorContainer"
						class="flex w-full flex-wrap justify-end gap-2 md:gap-5"
					>
						<CarouselItemSelector
							v-for="(_, index) in scrollableSlidesNumber"
							:key="`selector-${index}`"
							:active="currentCarouselItemIndex === index"
							:delay="2000"
							:dynamic-width="carouselDynamicItemWidth"
							@click="scrollTo(index)"
						/>
					</div>

					<div class="flex gap-0">
						<CarouselGenericAction @click="handlePreviousSlideArrowClick">
							<IconMaterialSymbolsChevronLeft class="size-7 md:size-8" />
						</CarouselGenericAction>
						<CarouselGenericAction @click="handleNextSlideArrowClick">
							<IconMaterialSymbolsChevronRight class="size-7 md:size-8" />
						</CarouselGenericAction>
					</div>
				</div>
			</Carousel>
		</div>
	</section>
</template>
